import React, { useState, useEffect } from "react";
import { Modal, Form, Input, Select, DatePicker, Radio, Switch, message, Card } from "antd";
import Button from "../../../components/common/button";
import Dropdown from "../../../components/common/dropdown";
import { useSelector, useDispatch } from "react-redux";
import { fetchBusiness, reset } from "../../../store/slices/businessSlice";
import { getBusinessServices } from "../../../api/service";
import { createCartDiscountRule, updateCartDiscountRule } from "../../../api/cartDiscountRules";
import colors from "../../../share/constants/constants";
import dayjs from "dayjs";

const { TextArea } = Input;
const { Option } = Select;

const CartDiscountRuleForm = ({ open, onCancel, onSuccess, rule, isEdit }) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const businessNames = useSelector((state) => state?.business?.res?.items);
  const isBusinessLoading = useSelector((state) => state?.business?.isLoading);

  const [ruleType, setRuleType] = useState(rule?.ruleType || "discount");
  const [selectBusinessId, setSelectBusinessId] = useState(null);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [services, setServices] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [isServiceLoading, setIsServiceLoading] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      const payload = {
        pageNumber: 1,
        itemPerPage: 10,
      };
      dispatch(fetchBusiness(payload));
      
      if (isEdit && rule) {
        const vendorId = rule.vendorId?._id || rule.vendorId;
        const serviceId = rule.serviceId?._id || rule.serviceId;
        
        setRuleType(rule.ruleType || "discount");
        setSelectBusinessId(vendorId);
        setSelectedBusiness(null);
        setServices([]);
        setSelectedService(serviceId || null);
        
        form.setFieldsValue({
          vendorId: vendorId,
          ruleType: rule.ruleType || "discount",
          serviceId: serviceId,
          minimumAmount: rule.minimumAmount,
          discountType: rule.discountType,
          discountValue: rule.discountValue,
          bannerText: rule.bannerText,
          startAt: rule.startAt ? dayjs(rule.startAt) : null,
          endAt: rule.endAt ? dayjs(rule.endAt) : null,
          isEnabled: rule.isEnabled !== undefined ? rule.isEnabled : true,
        });

        if (vendorId) {
          getServicesByBusinessId(vendorId, 1).then(() => {
            if (serviceId) {
              form.setFieldsValue({ serviceId: serviceId });
            }
          });
        }
      } else {
        form.resetFields();
        setRuleType("discount");
        setSelectBusinessId(null);
        setSelectedBusiness(null);
        setServices([]);
        setSelectedService(null);
        form.setFieldsValue({
          ruleType: "discount",
          isEnabled: true,
        });
      }
    }
  }, [open, rule, isEdit, dispatch, form]);

  useEffect(() => {
    if (!open) {
      form.resetFields();
      setRuleType("discount");
      setSelectBusinessId(null);
      setSelectedBusiness(null);
      setServices([]);
      setSelectedService(null);
    }
  }, [open, form]);

  const loadMoreData = (page, itemPerPage = 10, text = "") => {
    const payload = {
      pageNumber: page,
      itemPerPage: itemPerPage,
    };
    if (text) {
      payload.text = text;
      payload.isLoadMore = true;
    }
    dispatch(fetchBusiness(payload));
  };

  const fetchBusinessByName = (payload) => {
    if (payload?.pageNumber === 1 && payload?.text) {
      dispatch(reset());
    }
    dispatch(fetchBusiness(payload));
  };

  const onDropDownClose = () => {
    dispatch(reset());
    const payload = {
      pageNumber: 1,
      itemPerPage: 10,
    };
    dispatch(fetchBusiness(payload));
  };

  const getServicesByBusinessId = async (businessId, page) => {
    setIsServiceLoading(true);
    const res = await getBusinessServices({
      pageNumber: page,
      pageSize: 50,
      businessId: businessId,
    });
    if (res) {
      setServices((prevServices) => [...prevServices, ...res?.items]);
    }
    setIsServiceLoading(false);
  };

  const handleBusinessChange = async (businessId) => {
    setSelectBusinessId(businessId);
    setSelectedBusiness((businessNames || []).find((b) => b._id === businessId));
    setSelectedService(null);
    setServices([]);
    form.setFieldsValue({ serviceId: undefined });
    if (businessId) {
      await getServicesByBusinessId(businessId, 1);
    }
  };

  const handleServiceChange = (serviceId) => {
    const service = services.find((s) => s._id === serviceId);
    setSelectedService(service);
  };

  const loadMoreServiceData = async (page) => {
    await getServicesByBusinessId(selectBusinessId, page);
  };

  const handleRuleTypeChange = (e) => {
    const newType = e.target.value;
    setRuleType(newType);
    form.setFieldsValue({
      serviceId: undefined,
      discountType: undefined,
      discountValue: undefined,
      bannerText: undefined,
    });
    setSelectedService(null);
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const payload = {
        vendorId: values.vendorId,
        ruleType: values.ruleType,
        minimumAmount: parseFloat(values.minimumAmount),
        startAt: values.startAt ? dayjs(values.startAt).toISOString() : null,
        endAt: values.endAt ? dayjs(values.endAt).toISOString() : null,
        isEnabled: values.isEnabled !== undefined ? values.isEnabled : true,
      };

      if (values.ruleType === "discount") {
        if (!values.serviceId) {
          message.error("Please select a service or deal");
          setLoading(false);
          return;
        }
        payload.serviceId = values.serviceId;
        payload.discountType = values.discountType;
        payload.discountValue = parseFloat(values.discountValue);
        payload.bannerText = values.bannerText;
      } else {
        payload.bannerText = values.bannerText;
        if (values.serviceId) {
          payload.serviceId = values.serviceId;
        }
      }

      if (isEdit && rule?._id) {
        await updateCartDiscountRule(rule._id, payload);
        message.success("Rule updated successfully");
      } else {
        await createCartDiscountRule(payload);
        message.success("Rule created successfully");
      }

      onSuccess && onSuccess();
      onCancel();
    } catch (error) {
      console.error("Error saving rule:", error);
    } finally {
      setLoading(false);
    }
  };


  return (
    <Modal
      title={isEdit ? "Edit Cart Discount Rule" : "Create Cart Discount Rule"}
      open={open}
      onCancel={onCancel}
      footer={null}
      width={700}
    >
      <Card className="border-none">
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          className="pt-4"
        >
          <Form.Item
            name="vendorId"
            label="Vendor"
            rules={[
              {
                required: true,
                message: "Please select a vendor",
              },
            ]}
          >
            <Dropdown
              className={"h-5 !rounded-lg"}
              placeholder="Choose Vendor"
              isBusiness={true}
              loadMoreData={(e, itemPerPage, text) => {
                loadMoreData(e, itemPerPage, text);
              }}
              data={businessNames || []}
              onChange={handleBusinessChange}
              loader={isBusinessLoading}
              fetchData={fetchBusinessByName}
              onDropDownClose={onDropDownClose}
            />
          </Form.Item>

          <Form.Item
            name="ruleType"
            label="Rule Type"
            rules={[
              {
                required: true,
                message: "Please select a rule type",
              },
            ]}
          >
            <Radio.Group onChange={handleRuleTypeChange} value={ruleType}>
              <Radio value="discount">Deal / Service Discount Rule</Radio>
              <Radio value="informational">Informational Vendor Banner Rule</Radio>
            </Radio.Group>
          </Form.Item>

          {ruleType === "discount" && (
            <>
              <Form.Item
                name="serviceId"
                label="Service or Deal"
                rules={[
                  {
                    required: true,
                    message: "Please select a service or deal",
                  },
                ]}
              >
                <Dropdown
                  className={"h-5 !rounded-lg"}
                  placeholder="Choose Service or Deal"
                  isService={true}
                  loadMoreData={(e) => {
                    loadMoreServiceData(e);
                  }}
                  data={services}
                  onChange={handleServiceChange}
                  loader={isServiceLoading}
                  disabled={!selectBusinessId}
                />
              </Form.Item>

              <Form.Item
                name="discountType"
                label="Discount Type"
                rules={[
                  {
                    required: true,
                    message: "Please select discount type",
                  },
                ]}
              >
                <Select placeholder="Select discount type" className="!h-5.4 !rounded-xl">
                  <Option value="percentage">Percentage</Option>
                  <Option value="flat">Flat Amount</Option>
                </Select>
              </Form.Item>

              <Form.Item
                name="discountValue"
                label="Discount Value"
                rules={[
                  {
                    required: true,
                    message: "Please enter discount value",
                  },
                  {
                    pattern: /^(0*[1-9]\d*(\.\d+)?|0+\.\d*[1-9]\d*)$/,
                    message: "Please enter a positive number",
                  },
                ]}
              >
                <Input
                  type="number"
                  placeholder="Enter discount value"
                  className="w-full h-5.4 !rounded-xl border-unset"
                />
              </Form.Item>

              <Form.Item
                name="bannerText"
                label="Banner Text Template"
                rules={[
                  {
                    required: true,
                    message: "Please enter banner text template",
                  },
                ]}
                tooltip="Use ${remainingAmount} and ${rule.value} as placeholders"
              >
                <TextArea
                  placeholder="e.g., Add $${remainingAmount} to get ${rule.value}% off"
                  autoSize={{ minRows: 2, maxRows: 4 }}
                  className="w-full h-5.4 !rounded-xl border-unset"
                />
              </Form.Item>
            </>
          )}

          {ruleType === "informational" && (
            <>
              <Form.Item
                name="serviceId"
                label="Service or Deal (Optional)"
              >
                <Dropdown
                  className={"h-5 !rounded-lg"}
                  placeholder="Choose Service or Deal (Optional)"
                  isService={true}
                  loadMoreData={(e) => {
                    loadMoreServiceData(e);
                  }}
                  data={services}
                  onChange={handleServiceChange}
                  loader={isServiceLoading}
                  disabled={!selectBusinessId}
                />
              </Form.Item>

              <Form.Item
                name="bannerText"
                label="Banner Message"
                rules={[
                  {
                    required: true,
                    message: "Please enter banner message",
                  },
                ]}
              >
                <TextArea
                  placeholder="Enter static banner message"
                  autoSize={{ minRows: 2, maxRows: 4 }}
                  className="w-full h-5.4 !rounded-xl border-unset"
                />
              </Form.Item>
            </>
          )}

          <Form.Item
            name="minimumAmount"
            label="Minimum Vendor Cart Amount"
            rules={[
              {
                required: true,
                message: "Please enter minimum cart amount",
              },
              {
                pattern: /^(0*[1-9]\d*(\.\d+)?|0+\.\d*[1-9]\d*)$/,
                message: "Please enter a positive number",
              },
            ]}
          >
            <Input
              type="number"
              placeholder="Enter minimum cart amount"
              className="w-full h-5.4 !rounded-xl border-unset"
            />
          </Form.Item>

          <div className="grid grid-cols-2 gap-x-2">
            <Form.Item
              name="startAt"
              label="Start Date & Time"
              rules={[
                {
                  required: true,
                  message: "Please select start date and time",
                },
              ]}
            >
              <DatePicker
                showTime={{ format: "HH:mm" }}
                format="YYYY-MM-DD HH:mm"
                placeholder="Select start date and time"
                className="w-full"
              />
            </Form.Item>

            <Form.Item
              name="endAt"
              label="End Date & Time"
              rules={[
                {
                  required: true,
                  message: "Please select end date and time",
                },
              ]}
            >
              <DatePicker
                showTime={{ format: "HH:mm" }}
                format="YYYY-MM-DD HH:mm"
                placeholder="Select end date and time"
                className="w-full"
              />
            </Form.Item>
          </div>

          <Form.Item
            name="isEnabled"
            label="Status"
            valuePropName="checked"
            initialValue={true}
          >
            <Switch
              checkedChildren="Enabled"
              unCheckedChildren="Disabled"
            />
          </Form.Item>

          <Form.Item className="flex justify-center mb-0">
            <Button
              title={isEdit ? "Update" : "Save"}
              htmlType="submit"
              color={colors.primaryPurple}
              borderRadius={5}
              className="flex items-center w-full !rounded-xl"
              loading={loading}
            />
          </Form.Item>
        </Form>
      </Card>
    </Modal>
  );
};

export default CartDiscountRuleForm;
